import { State, Action, StateContext, Selector, NgxsOnInit } from '@ngxs/store';
import { ViewActions } from './view.actions';
import { ViewBreakpoint } from '../models/view-breakpoint.enum';
import { ViewLayoutModel, LayoutDefault, LogoLayout, DrawerLayout } from '../models/view-layout.model';
import { Fullscreen } from 'src/app/shared/libraries/fullscreen/fullscreen';
import { ColorRecipeModel } from 'src/app/edit/models/color-recipe.model';
import { TreeSearchDisplayMode } from 'src/app/shared/components/tree/tree-search/tree-search.component';
import { ViewLayoutStatus } from '../models/view-layout-status.enum';
import { LoaderTypeEnum } from '../models/loader-type.enum';




export class ViewStateModel {
  layout: ViewLayoutModel;
  initialLayout: ViewLayoutModel;

  breakpoint: ViewBreakpoint;
  labelsBreakpoint: number;
  inputEnabled: boolean;
  drawerCoversContent: boolean;
  fullscreenActive: boolean;
  presentationActive: boolean;
  showNodeInfo: boolean;
  showHelp: boolean;
  loaderActive: boolean;
  loaderType: LoaderTypeEnum;
  usePrintImage: boolean;
  multiPickModeEnabled: boolean;
  searchDisplayMode: TreeSearchDisplayMode;
  canHover: boolean;
  canTouch: boolean;
  canFullscreen: boolean;
}

export const initialLayout: ViewLayoutModel = {
  audio: new LayoutDefault(),
  camera: new LayoutDefault(ViewLayoutStatus.Show, true), // Camera controls - zoom, pan, reset. Off by default.
  chapterList: new LayoutDefault(ViewLayoutStatus.Show, false),
  column: new LayoutDefault(), // Left description panel, visible on desktop mode
  description: new LayoutDefault(),
  drawer: new DrawerLayout(), // Bottom description panel, visible at 960 below
  fullscreen: new LayoutDefault(),
  help: new LayoutDefault(),
  labelInfo: new LayoutDefault(),
  labelList: new LayoutDefault(ViewLayoutStatus.Show, false),
  logo: new LogoLayout(),
  media: new LayoutDefault(), // Play, pause, scrubber
  menu: new LayoutDefault(), // Menu button and child items, visible at 960 below
  nodeInfo: new LayoutDefault(ViewLayoutStatus.Hide, false),
  pagination: new LayoutDefault(),
  presenter: new LayoutDefault(ViewLayoutStatus.Hide, false),
  reset: new LayoutDefault(),
  search: new LayoutDefault(),
  toolbar: new LayoutDefault(), // Interaction tools, visible in drawer when responsive, floating on the scene in desktop
  tools: new LayoutDefault(), // tool bar button in menu
};

export const initialViewState: ViewStateModel = {
  breakpoint: null,
  labelsBreakpoint: 480,
  inputEnabled: true,
  fullscreenActive: false,
  presentationActive: false,
  drawerCoversContent: false,
  searchDisplayMode: TreeSearchDisplayMode.List,

  showNodeInfo: false,
  showHelp: false,
  loaderActive: false,
  loaderType: LoaderTypeEnum.Play,

  usePrintImage: false,

  multiPickModeEnabled: false,

  canHover: false,
  canTouch: false,
  canFullscreen: false,

  layout: initialLayout,
  initialLayout: initialLayout
};

@State<ViewStateModel>({
  name: 'view',
  defaults: initialViewState
})
export class ViewState implements NgxsOnInit {
  // ---------- Selectors ----------
  @Selector()
  static getLayout(state: ViewStateModel) {
    return state.layout;
  }

  @Selector()
  static getInitialLayout(state: ViewStateModel) {
    return state.initialLayout;
  }

  @Selector()
  static getBreakpoint(state: ViewStateModel) {
    return state.breakpoint;
  }

  @Selector()
  static getLabelsBreakpoint(state: ViewStateModel) {
    return state.labelsBreakpoint;
  }

  @Selector()
  static getInputEnabled(state: ViewStateModel) {
    return state.inputEnabled;
  }

  @Selector()
  static getFullscreenActive(state: ViewStateModel) {
    return state.fullscreenActive;
  }

  @Selector()
  static getPresentationActive(state: ViewStateModel) {
    return state.presentationActive;
  }

  @Selector()
  static getDrawerCoversContent(state: ViewStateModel) {
    return state.drawerCoversContent;
  }

  @Selector()
  static getShowNodeInfo(state: ViewStateModel) {
    return state.showNodeInfo;
  }

  @Selector()
  static getShowHelp(state: ViewStateModel) {
    return state.showHelp;
  }

  @Selector()
  static getLoaderActive(state: ViewStateModel) {
    return state.loaderActive;
  }

  @Selector()
  static getLoaderType(state: ViewStateModel) {
    return state.loaderType;
  }

  @Selector()
  static getCanTouch(state: ViewStateModel) {
    return state.canTouch;
  }

  @Selector()
  static getCanHover(state: ViewStateModel) {
    return state.canHover;
  }

  @Selector()
  static getMultiPickModeEnabled(state: ViewStateModel) {
    return state.multiPickModeEnabled;
  }

  @Selector()
  static getSearchDisplayMode(state: ViewStateModel) {
    return state.searchDisplayMode;
  }

  @Selector()
  static getUsePrintImage(state: ViewStateModel) {
    return state.usePrintImage;
  }

  // ---------- Actions ----------
  @Action(ViewActions.SetLayout)
  setLayout(
    context: StateContext<ViewStateModel>,
    action: ViewActions.SetLayout
  ) {
    const layout = context.getState().layout;
    context.patchState({
      layout: {
        ...layout,
        ...action.layout
      }
    });
  }

  @Action(ViewActions.SetInitialLayout)
  setInitialLayout(context: StateContext<ViewStateModel>, { layout }: ViewActions.SetInitialLayout) {
    context.patchState({
      initialLayout: layout
    });
  }

  @Action(ViewActions.SetBreakpoint)
  setBreakpoint(
    context: StateContext<ViewStateModel>,
    action: ViewActions.SetBreakpoint
  ) {
    context.patchState({
      breakpoint: action.breakpoint
    });
  }

  @Action(ViewActions.SetInputEnabled)
  setInputEnabled(
    context: StateContext<ViewStateModel>,
    action: ViewActions.SetInputEnabled
  ) {
    context.patchState({
      inputEnabled: action.enabled
    });
  }

  @Action(ViewActions.SetCanHover)
  setCanHover(
    context: StateContext<ViewStateModel>,
    action: ViewActions.SetCanHover
  ) {
    context.patchState({
      canHover: action.canHover
    });
  }

  @Action(ViewActions.SetCanTouch)
  setCanTouch(
    context: StateContext<ViewStateModel>,
    action: ViewActions.SetCanTouch
  ) {
    context.patchState({
      canTouch: action.canTouch
    });
  }

  @Action(ViewActions.SetCanFullscreen)
  setCanFullscreen(
    context: StateContext<ViewStateModel>,
    action: ViewActions.SetCanFullscreen
  ) {
    context.patchState({
      canFullscreen: action.canFullscreen
    });
  }

  @Action(ViewActions.SetSearchDisplayMode)
  setSearchDisplayMode(
    context: StateContext<ViewStateModel>,
    action: ViewActions.SetSearchDisplayMode
  ) {
    context.patchState({
      searchDisplayMode: action.mode
    });
  }

  @Action(ViewActions.ToggleNodeInfo)
  toggleNodeInfo(
    context: StateContext<ViewStateModel>,
    action: ViewActions.ToggleNodeInfo
  ) {
    context.patchState({
      showNodeInfo: action.show
    });
  }

  @Action(ViewActions.TogglePresentation)
  togglePresentation(
    context: StateContext<ViewStateModel>,
    action: ViewActions.TogglePresentation
  ) {
    context.patchState({
      presentationActive: action.active
    });
  }

  @Action(ViewActions.ToggleHelp)
  toggleHelp(
    context: StateContext<ViewStateModel>,
    action: ViewActions.ToggleHelp
  ) {
    context.patchState({
      showHelp: action.show
    });
  }

  @Action(ViewActions.ToggleLoader)
  toggleLoader(
    context: StateContext<ViewStateModel>,
    action: ViewActions.ToggleLoader
  ) {
    context.patchState({
      loaderActive: action.show
    });
  }

  @Action(ViewActions.SetLoaderType)
  setLoaderType(
    context: StateContext<ViewStateModel>,
    action: ViewActions.SetLoaderType
  ) {
    context.patchState({
      loaderType: action.loaderType
    });
  }

  @Action(ViewActions.SetMultiPickModeEnabled)
  setMultiPickModeEnabled(
    context: StateContext<ViewStateModel>,
    action: ViewActions.SetMultiPickModeEnabled
  ) {
    context.patchState({
      multiPickModeEnabled: action.enabled
    });
  }

  @Action(ViewActions.SetLabelBreakpoint)
  setLabelBreakpoint(context: StateContext<ViewStateModel>, action: ViewActions.SetLabelBreakpoint) {
    context.patchState({
      labelsBreakpoint: action.labelsBreakpoint
    });
  }

  @Action(ViewActions.SetUsePrintImage)
  setUsePrintImage(context: StateContext<ViewStateModel>, action: ViewActions.SetUsePrintImage) {
    context.patchState({
      usePrintImage: action.usePrintImage
    });
  }

  ngxsOnInit(context: StateContext<ViewStateModel>) {
    const fullscreen$ = Fullscreen.onFullscreenChange();

    fullscreen$.subscribe(event => {
      context.patchState({
        fullscreenActive: Fullscreen.isFullscreen()
      });
    });
  }
}
